import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Archive,
  BarChart3,
  BookOpenText,
  Check,
  ChevronDown,
  CornerDownRight,
  CircleHelp,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  LockKeyhole,
  LogOut,
  Menu,
  MessageSquareText,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import { demoWorkspace } from "./data/demo";
import {
  createDemoValidationQueue,
  demoUdrTasks,
  demoValidationObservation,
  demoValidationStatus,
  DEMO_PERSONAS,
  type DemoPersona,
} from "./data/demoFlow";
import { AdminPanel } from "./components/AdminPanel";
import { ValidationPanel } from "./components/ValidationPanel";
import "./change-direction.css";
import { isSupabaseConfigured, supabase } from "./lib/supabase";
import {
  closeReprogramming,
  loadWorkspace,
  saveTargetValue,
  saveTaskJustification,
  setMonthEnabled,
  setRegistrationEnabled,
  signIn,
  signOut,
} from "./services/poi";
import type { ValidationQueueItem } from "./services/validations";
import { MONTHS, type TaskRow, type WorkspaceData } from "./types";
import { encodeExcelCsv } from "./utils/csv";
import { buildVersionReportRows } from "./utils/report";
import { isReviewerCenter, isUdrCode } from "./utils/validation";

type View = "editor" | "review" | "admin";
type SaveState = "idle" | "saving" | "saved" | "error";

function canReview(workspace: WorkspaceData) {
  return workspace.profile.role === "admin" || workspace.costCenters.some((center) => isReviewerCenter(center.code));
}

const VALIDATION_LABELS = {
  pending_gmr: "Pendiente de validación GMR",
  pending_line: "Pendiente de órgano de línea",
  observed_gmr: "Observada por la GMR",
  observed_line: "Observada por órgano de línea",
  accepted: "Reprogramación aceptada",
} as const;

function App() {
  const [sessionReady, setSessionReady] = useState(!isSupabaseConfigured);
  const [signedIn, setSignedIn] = useState(!isSupabaseConfigured);
  const [workspace, setWorkspace] = useState<WorkspaceData | null>(
    isSupabaseConfigured ? null : structuredClone(demoWorkspace),
  );
  const [demoPersona, setDemoPersona] = useState<DemoPersona>("admin");
  const [demoValidations, setDemoValidations] = useState<ValidationQueueItem[]>([]);
  const [view, setView] = useState<View>("editor");
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(Boolean(data.session));
      setSessionReady(true);
      if (data.session) void refresh();
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(Boolean(session));
      setSessionReady(true);
      if (session) void refresh();
      else setWorkspace(null);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  async function refresh(centerId?: string) {
    if (!isSupabaseConfigured) {
      setWorkspace((current) => current ? { ...current, selectedCenterId: centerId ?? current.selectedCenterId } : current);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setWorkspace(await loadWorkspace(centerId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo cargar la reprogramación.");
    } finally {
      setLoading(false);
    }
  }

  function changeDemoPersona(persona: DemoPersona) {
    const selected = DEMO_PERSONAS[persona];
    setDemoPersona(persona);
    setWorkspace((current) => {
      if (!current) return current;
      const status = demoValidationStatus(demoValidations);
      const observed = status === "observed_gmr" || status === "observed_line";
      return {
        ...current,
        profile: selected.profile,
        costCenters: [selected.center],
        selectedCenterId: selected.center.id,
        tasks: persona === "udr" && !current.tasks.some((task) => task.id.startsWith("demo-udr-"))
          ? structuredClone(demoUdrTasks)
          : current.tasks,
        submissionStatus: persona === "udr" ? (observed ? "draft" : demoValidations.length ? "closed" : "draft") : current.submissionStatus,
        allowedMonths: persona === "udr" && observed ? current.cycle.months.map((month) => month.month) : null,
        validationStatus: persona === "udr" ? status : current.validationStatus,
        validationObservation: persona === "udr" ? demoValidationObservation(demoValidations) : current.validationObservation,
      };
    });
    setView(persona === "admin" ? "admin" : persona === "udr" ? "editor" : "review");
  }

  function updateDemoValidations(nextRows: ValidationQueueItem[]) {
    setDemoValidations(nextRows);
    const status = demoValidationStatus(nextRows);
    const observed = status === "observed_gmr" || status === "observed_line";
    setWorkspace((current) => current ? {
      ...current,
      submissionStatus: observed ? "draft" : current.submissionStatus,
      allowedMonths: observed ? current.cycle.months.map((month) => month.month) : current.allowedMonths,
      validationStatus: status,
      validationObservation: demoValidationObservation(nextRows),
    } : current);
  }

  function submitDemoUdr(versionNumber: number, tasks: TaskRow[], taskJustifications: Record<string, string>) {
    updateDemoValidations(createDemoValidationQueue(tasks, versionNumber, taskJustifications));
  }

  if (!sessionReady) return <Splash message="Comprobando sesión…" />;
  if (!signedIn) return <Login />;
  if (loading && !workspace) return <Splash message="Preparando las metas del POI…" />;
  if (error && !workspace) return <ErrorScreen message={error} onRetry={() => void refresh()} />;
  if (!workspace) return null;

  return (
    <div className="app-shell">
      <Sidebar
        workspace={workspace}
        view={view}
        onView={setView}
        onSignOut={() => isSupabaseConfigured ? void signOut() : undefined}
      />
      <main className="main-content">
        {!isSupabaseConfigured && (
          <div className="demo-banner">
            <span className="demo-mode"><Sparkles size={15} /> Modo demostración</span>
            <label className="demo-persona">Ver como
              <select aria-label="Perfil de demostración" value={demoPersona} onChange={(event) => changeDemoPersona(event.target.value as DemoPersona)}>
                {(Object.keys(DEMO_PERSONAS) as DemoPersona[]).map((key) => <option key={key} value={key}>{DEMO_PERSONAS[key].label}</option>)}
              </select>
            </label>
            <span className="demo-connect">Conecta Supabase en <code>.env.local</code></span>
          </div>
        )}
        {error && <div className="inline-error"><X size={16} />{error}</div>}
        {view === "editor" ? (
          <Editor
            workspace={workspace}
            onWorkspaceChange={setWorkspace}
            onCenterChange={(id) => void refresh(id)}
            onDemoUdrClosed={submitDemoUdr}
          />
        ) : view === "review" ? (
          <ValidationPanel workspace={workspace} demoRows={demoValidations} onDemoRowsChange={updateDemoValidations} />
        ) : (
          <AdminPanel workspace={workspace} onWorkspaceChange={setWorkspace} />
        )}
      </main>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await signIn(email, password);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible iniciar sesión.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="brand-mark"><FileSpreadsheet size={25} /></div>
        <span>POI 2026</span>
      </div>
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-heading">
          <span className="eyebrow">SISTEMA INSTITUCIONAL</span>
          <h1>Reprogramación de metas físicas</h1>
          <p>Ingresa con la cuenta asignada a tu centro de costo.</p>
        </div>
        <label>Correo institucional<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label>Contraseña<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {message && <div className="form-error">{message}</div>}
        <button className="primary-button full" disabled={busy}>{busy ? "Ingresando…" : "Ingresar"}</button>
        <p className="auth-help"><LockKeyhole size={14} /> El acceso está restringido a usuarios autorizados.</p>
      </form>
    </div>
  );
}

function Sidebar({ workspace, view, onView, onSignOut }: {
  workspace: WorkspaceData;
  view: View;
  onView: (view: View) => void;
  onSignOut: () => void;
}) {
  const demoReviewer = !isSupabaseConfigured && (workspace.profile.id === "demo-gmr" || workspace.profile.id === "demo-grep");
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="mobile-menu" onClick={() => setOpen(true)} aria-label="Abrir menú"><Menu /></button>
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-brand"><div className="brand-mark small"><FileSpreadsheet size={19} /></div><span>POI 2026</span><button onClick={() => setOpen(false)}><X /></button></div>
        <nav>
          {!demoReviewer && <button className={view === "editor" ? "active" : ""} onClick={() => { onView("editor"); setOpen(false); }}><BarChart3 />Reprogramación</button>}
          {canReview(workspace) && <button className={view === "review" ? "active" : ""} onClick={() => { onView("review"); setOpen(false); }}><ClipboardCheck />Validaciones</button>}
          {workspace.profile.role === "admin" && <button className={view === "admin" ? "active" : ""} onClick={() => { onView("admin"); setOpen(false); }}><Settings2 />Administración</button>}
        </nav>
        <div className="sidebar-spacer" />
        <div className="sidebar-note"><CircleHelp /><div><strong>¿Necesitas ayuda?</strong><span>Consulta la guía del proceso.</span></div></div>
        <div className="profile-card">
          <div className="avatar">{workspace.profile.fullName.split(" ").slice(0, 2).map((part) => part[0]).join("")}</div>
          <div><strong>{workspace.profile.fullName}</strong><span>{workspace.profile.role === "admin" ? "Administrador" : "Responsable"}</span></div>
          <button onClick={onSignOut} title="Cerrar sesión"><LogOut size={17} /></button>
        </div>
      </aside>
      {open && <button className="sidebar-backdrop" onClick={() => setOpen(false)} aria-label="Cerrar menú" />}
    </>
  );
}

function Editor({ workspace, onWorkspaceChange, onCenterChange, onDemoUdrClosed }: {
  workspace: WorkspaceData;
  onWorkspaceChange: React.Dispatch<React.SetStateAction<WorkspaceData | null>>;
  onCenterChange: (id: string) => void;
  onDemoUdrClosed?: (versionNumber: number, tasks: TaskRow[], taskJustifications: Record<string, string>) => void;
}) {
  const [search, setSearch] = useState("");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [editedSinceLoad, setEditedSinceLoad] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [documentReference, setDocumentReference] = useState("");
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [selectedDefinitionTask, setSelectedDefinitionTask] = useState<TaskRow | null>(null);
  const [expandedJustificationTaskId, setExpandedJustificationTaskId] = useState<string | null>(null);
  const [collapsedActivityKeys, setCollapsedActivityKeys] = useState<Set<string>>(() => new Set());
  const center = workspace.costCenters.find((item) => item.id === workspace.selectedCenterId)!;
  const locked = workspace.submissionStatus === "closed" || !workspace.cycle.registrationEnabled || workspace.cycle.status !== "open";

  const filteredTasks = useMemo(() => workspace.tasks.filter((task) => {
    const query = search.trim().toLocaleLowerCase("es");
    const matches = !query || `${task.taskCode} ${task.taskName} ${task.activityName}`.toLocaleLowerCase("es").includes(query);
    const changed = task.modified.some((value) => value !== null);
    return matches && (!onlyChanged || changed);
  }), [workspace.tasks, search, onlyChanged]);
  const groupedTasks = useMemo(() => {
    const groups: Array<{ key: string; code: string; name: string; tasks: TaskRow[] }> = [];
    const byActivity = new Map<string, (typeof groups)[number]>();
    for (const task of filteredTasks) {
      const key = `${task.activityCode}|${task.activityName}`;
      let group = byActivity.get(key);
      if (!group) {
        group = { key, code: task.activityCode, name: task.activityName, tasks: [] };
        byActivity.set(key, group);
        groups.push(group);
      }
      group.tasks.push(task);
    }
    return groups;
  }, [filteredTasks]);


  const changedTasks = workspace.tasks.filter((task) => task.modified.some((value) => value !== null)).length;
  const changedCells = workspace.tasks.reduce((total, task) => total + task.modified.filter((value) => value !== null).length, 0);
  const incompleteJustificationTasks = workspace.tasks.filter((task) => task.modified.some((value) => value !== null) && (workspace.taskJustifications[task.id] ?? "").trim().length < 5);
  const incompleteJustifications = incompleteJustificationTasks.length;

  function expandActivityForTask(task: TaskRow) {
    const activityKey = `${task.activityCode}|${task.activityName}`;
    setCollapsedActivityKeys((current) => {
      if (!current.has(activityKey)) return current;
      const next = new Set(current);
      next.delete(activityKey);
      return next;
    });
  }

  function openJustification(task: TaskRow) {
    expandActivityForTask(task);
    setExpandedJustificationTaskId(task.id);
  }

  function toggleActivity(activityKey: string) {
    setCollapsedActivityKeys((current) => {
      const next = new Set(current);
      if (next.has(activityKey)) next.delete(activityKey);
      else next.add(activityKey);
      return next;
    });
  }

  function updateLocal(taskId: string, monthIndex: number, rawValue: string) {
    const parsed = rawValue === "" ? 0 : Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    const editedTask = workspace.tasks.find((task) => task.id === taskId);
    if (editedTask) {
      const remainsChanged = editedTask.modified.some((value, index) => index === monthIndex ? parsed !== editedTask.baseline[index] : value !== null);
      if (remainsChanged) openJustification(editedTask);
      else setExpandedJustificationTaskId((current) => current === taskId ? null : current);
    }
    onWorkspaceChange((current) => current ? {
      ...current,
      tasks: current.tasks.map((task) => {
        if (task.id !== taskId) return task;
        const modified = [...task.modified];
        modified[monthIndex] = parsed === task.baseline[monthIndex] ? null : parsed;
        return { ...task, modified };
      }),
    } : current);
    setSaveState("idle");
    setEditedSinceLoad(true);
  }

  async function commit(task: TaskRow, monthIndex: number) {
    if (!isSupabaseConfigured) { setSaveState("saved"); return; }
    const value = task.modified[monthIndex] ?? task.baseline[monthIndex];
    setSaveState("saving");
    try {
      const submissionId = await saveTargetValue(workspace.cycle.id, center.id, task.id, monthIndex + 1, value);
      onWorkspaceChange((current) => current ? { ...current, submissionId } : current);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }

  function updateJustification(taskId: string, value: string) {
    onWorkspaceChange((current) => current ? {
      ...current,
      taskJustifications: { ...current.taskJustifications, [taskId]: value },
    } : current);
    setSaveState("idle");
    setEditedSinceLoad(true);
  }

  async function commitJustification(taskId: string) {
    if (!isSupabaseConfigured) { setSaveState("saved"); return; }
    setSaveState("saving");
    try {
      await saveTaskJustification(workspace.cycle.id, center.id, taskId, workspace.taskJustifications[taskId] ?? "");
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }

  async function close() {
    const changed = workspace.tasks.filter((task) => task.modified.some((value) => value !== null));
    if (changed.some((task) => (workspace.taskJustifications[task.id] ?? "").trim().length < 5) || documentReference.trim().length < 3) return;
    setClosing(true);
    setCloseError(null);
    try {
      const versionId = isSupabaseConfigured ? await closeReprogramming(workspace.cycle.id, center.id, workspace.taskJustifications, documentReference.trim()) : `demo-version-${Date.now()}`;
      const nextVersion = Math.max(0, ...workspace.versions.map((version) => version.versionNumber)) + 1;
      const version = {
        id: versionId, versionNumber: nextVersion, justification: "Sustentos registrados por tarea", documentReference: documentReference.trim(),
        createdByName: workspace.profile.fullName, createdAt: new Date().toISOString(),
        targets: changed.map((task) => ({ taskId: task.id, values: task.baseline.map((value, index) => task.modified[index] ?? value), justification: workspace.taskJustifications[task.id].trim() })),
      };
      const requiresValidation = isUdrCode(center.code);
      onWorkspaceChange((current) => current ? {
        ...current, submissionStatus: "closed", versions: [...current.versions, version],
        validationStatus: requiresValidation ? "pending_gmr" : "not_required",
        validationObservation: null,
      } : current);
      setEditedSinceLoad(false);
      if (!isSupabaseConfigured && requiresValidation) onDemoUdrClosed?.(nextVersion, workspace.tasks, workspace.taskJustifications);
      setCloseDialogOpen(false);
    } catch (cause) {
      setCloseError(cause instanceof Error ? cause.message : "No se pudo cerrar la reprogramación.");
    } finally {
      setClosing(false);
    }
  }

  function downloadReport() {
    const includeDraft = workspace.submissionStatus === "draft" && (workspace.versions.length === 0 || editedSinceLoad);
    const csvBody = buildVersionReportRows(workspace, center, includeDraft)
      .map((row) => row.map((cell) => typeof cell === "number" ? String(cell) : `"${String(cell).replaceAll('"', '""')}"`).join(";"))
      .join("\r\n");
    const csv = `sep=;\r\n${csvBody}`;
    const url = URL.createObjectURL(new Blob([encodeExcelCsv(csv)], { type: "text/csv;charset=utf-16le" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `historial-reprogramacion-${center.code.replaceAll(".", "-")}-${workspace.cycle.year}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page editor-page">
      <header className="page-header editor-header">
        <div><span className="eyebrow">PROGRAMACIÓN INSTITUCIONAL</span><h1>Reprogramación de metas</h1><p>Compara la programación vigente y registra únicamente los cambios necesarios.</p></div>
        <div className="header-actions">
          <span className={`status-pill ${workspace.validationStatus}`}><span />{
            workspace.validationStatus in VALIDATION_LABELS
              ? VALIDATION_LABELS[workspace.validationStatus as keyof typeof VALIDATION_LABELS]
              : workspace.submissionStatus === "closed" ? "Propuesta enviada"
                : workspace.allowedMonths ? "Reprogramación reabierta" : "Borrador en curso"
          }</span>
          <button className="secondary-button" onClick={downloadReport} disabled={!changedTasks && !workspace.versions.length}><Download size={17} />Descargar historial</button>
          <button className="primary-button" title={incompleteJustifications ? `Falta completar el sustento de ${incompleteJustifications} tarea(s)` : undefined} onClick={() => { setCloseError(null); setCloseDialogOpen(true); }} disabled={locked || !changedTasks || incompleteJustifications > 0}><LockKeyhole size={17} />Enviar reprogramación</button>
        </div>
      </header>

      <section className="workspace-overview">
      <div className="context-card">
        <div className="context-main"><span>Centro de costo</span><div className="select-wrap"><select value={workspace.selectedCenterId} onChange={(e) => { setCollapsedActivityKeys(new Set()); setExpandedJustificationTaskId(null); onCenterChange(e.target.value); }}>{workspace.costCenters.map((item) => <option key={item.id} value={item.id}>{item.code} · {item.name}</option>)}</select><ChevronDown /></div></div>
        <div className="context-meta"><div><span>Periodo</span><strong>{workspace.cycle.year}</strong></div><div><span>Proceso</span><strong>{workspace.cycle.name}</strong></div><div><span>Registro</span><strong className={workspace.cycle.registrationEnabled ? "success-text" : "danger-text"}>{workspace.cycle.registrationEnabled ? "Habilitado" : "Deshabilitado"}</strong></div></div>
      </div>
      {workspace.validationObservation && <div className="validation-observation"><strong>Observación recibida</strong><span>{workspace.validationObservation}</span></div>}

      <div className="summary-grid">
        <SummaryCard label="Tareas asignadas" value={workspace.tasks.length} detail="en este centro de costo" icon={<Archive />} />
        <SummaryCard label="Tareas modificadas" value={changedTasks} detail={`${changedCells} metas mensuales cambiadas`} icon={<SlidersHorizontal />} accent />
        <SummaryCard label="Avance del registro" value={`${workspace.tasks.length ? Math.round(changedTasks / workspace.tasks.length * 100) : 0}%`} detail="No es obligatorio modificar todas" icon={<Check />} />
      </div>
      </section>

      <section className="editor-card">
        {incompleteJustifications > 0 && <div className="justification-alert"><TriangleAlert /><div><strong>{incompleteJustifications === 1 ? "Falta 1 sustento por completar" : `Faltan ${incompleteJustifications} sustentos por completar`}</strong><span>Cada tarea modificada necesita una justificación antes del envío.</span></div><button type="button" onClick={() => { setSearch(""); setOnlyChanged(false); openJustification(incompleteJustificationTasks[0]); }}>Revisar pendientes</button></div>}
        <div className="editor-toolbar">
          <div className="search-box"><Search /><input placeholder="Buscar por código, actividad o tarea…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          <label className="check-filter"><input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />Solo modificadas</label>
          <span className={`save-indicator ${saveState}`}>{saveState === "saving" ? "Guardando…" : saveState === "error" ? "Error al guardar" : saveState === "saved" ? "Cambios guardados" : "Edición por mes"}</span>
        </div>
        <div className="legend"><span><i className="original-dot" />Meta original</span><span><i className="changed-dot" />Meta modificada</span><span>{filteredTasks.length} tareas visibles</span></div>
        <div className="table-scroll">
          <table className="targets-table">
            <thead><tr><th className="task-column">Tarea</th>{MONTHS.map((month, index) => {
              const enabled = Boolean(workspace.cycle.months[index]?.enabled) && (!workspace.allowedMonths || workspace.allowedMonths.includes(index + 1));
              return <th key={month} className={!enabled ? "disabled-month" : ""}>{month}{!enabled && <LockKeyhole size={11} />}</th>;
            })}<th>Total</th></tr></thead>
            <tbody>{groupedTasks.map((group) => <Fragment key={group.key}>
              <ActivityGroupRow code={group.code} name={group.name} taskCount={group.tasks.length} collapsed={collapsedActivityKeys.has(group.key)} onToggle={() => toggleActivity(group.key)} />
              {!collapsedActivityKeys.has(group.key) && group.tasks.map((task) => {
                const changed = task.modified.some((value) => value !== null);
                const justificationComplete = (workspace.taskJustifications[task.id] ?? "").trim().length >= 5;
                const expanded = expandedJustificationTaskId === task.id;
                return <Fragment key={task.id}>
                  <TaskTableRow task={task} workspace={workspace} locked={locked} onChange={updateLocal} onCommit={commit} onOpenDefinition={setSelectedDefinitionTask} justificationStatus={changed ? justificationComplete ? "complete" : "pending" : null} justificationExpanded={expanded} onToggleJustification={() => expanded ? setExpandedJustificationTaskId(null) : openJustification(task)} />
                  {changed && expanded && <TaskJustificationRow task={task} value={workspace.taskJustifications[task.id] ?? ""} locked={locked} onChange={updateJustification} onCommit={commitJustification} />}
                </Fragment>;
              })}
            </Fragment>)}</tbody>
          </table>
        </div>
        {!filteredTasks.length && <div className="empty-state"><Search /><strong>No encontramos tareas</strong><span>Prueba con otro término o desactiva el filtro.</span></div>}
      </section>

      {closeDialogOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => !closing && setCloseDialogOpen(false)}>
          <div className="reopen-modal close-version-modal" role="dialog" aria-modal="true" aria-labelledby="close-version-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div className="modal-icon"><LockKeyhole /></div><div><span className="eyebrow">ENVÍO DEL CENTRO DE COSTO</span><h2 id="close-version-title">Enviar reprogramación</h2></div><button onClick={() => setCloseDialogOpen(false)} disabled={closing}><X /></button></div>
            <div className="selected-center"><span>Centro de costo</span><strong>{center.code} · {center.name}</strong></div>
            <div className="close-version-number"><span>Estado del registro</span><strong>PROPUESTA ENVIADA</strong></div>
            <div className="justification-confirmation"><Check /><div><strong>{changedTasks} {changedTasks === 1 ? "tarea modificada" : "tareas modificadas"}</strong><span>Todos los sustentos individuales están completos y guardados.</span></div></div>
            <label className="document-field">Documento<input value={documentReference} onChange={(event) => setDocumentReference(event.target.value)} placeholder="Ej. MEMORANDO-001-2026" /><small>Ingrese el número o nombre del documento de sustento.</small></label>
            {closeError && <div className="form-error">{closeError}</div>}
            <div className="reopen-warning"><LockKeyhole /><span>Se guardará una fotografía para auditoría. Este envío todavía no tendrá número MOD; la numeración oficial será asignada únicamente cuando el administrador cierre el proceso institucional.</span></div>
            <div className="modal-actions"><button className="secondary-button" onClick={() => setCloseDialogOpen(false)} disabled={closing}>Cancelar</button><button className="primary-button" onClick={() => void close()} disabled={closing || incompleteJustifications > 0 || documentReference.trim().length < 3}><LockKeyhole />{closing ? "Enviando…" : "Enviar propuesta"}</button></div>
          </div>
        </div>
      )}
      {selectedDefinitionTask && <TaskDefinitionDrawer task={selectedDefinitionTask} onClose={() => setSelectedDefinitionTask(null)} />}
    </div>
  );
}
function ActivityGroupRow({ code, name, taskCount, collapsed, onToggle }: { code: string; name: string; taskCount: number; collapsed: boolean; onToggle: () => void }) {
  return <tr className={`activity-group-row ${collapsed ? "collapsed" : ""}`}><td colSpan={MONTHS.length + 2}><div className="activity-group-content">
    <span>Actividad operativa</span>
    <strong>{code} · {name}</strong>
    <small>{taskCount} {taskCount === 1 ? "tarea" : "tareas"}</small>
    <button type="button" className="activity-collapse-button" onClick={onToggle} aria-expanded={!collapsed} aria-label={`${collapsed ? "Mostrar" : "Ocultar"} tareas de ${code}`}>
      <span>{collapsed ? "Mostrar tareas" : "Ocultar tareas"}</span>
      <ChevronDown />
    </button>
  </div></td></tr>;
}


function TaskTableRow({ task, workspace, locked, onChange, onCommit, onOpenDefinition, justificationStatus, justificationExpanded, onToggleJustification }: {
  task: TaskRow;
  workspace: WorkspaceData;
  locked: boolean;
  onChange: (taskId: string, month: number, value: string) => void;
  onCommit: (task: TaskRow, month: number) => void;
  onOpenDefinition: (task: TaskRow) => void;
  justificationStatus: "pending" | "complete" | null;
  justificationExpanded: boolean;
  onToggleJustification: () => void;
}) {
  const total = task.baseline.reduce((sum, original, index) => sum + (task.modified[index] ?? original), 0);
  const originalTotal = task.baseline.reduce((sum, value) => sum + value, 0);
  return (
    <tr className="task-row">
      <td className="task-cell"><div className="task-title-line"><strong>{task.taskCode} · {task.taskName}</strong><div className="task-row-actions"><button type="button" className="definition-button" onClick={() => onOpenDefinition(task)} title="Ver definición operacional" aria-label={`Ver definición operacional de ${task.taskCode}`}><BookOpenText /></button>{justificationStatus && <button type="button" className={`justification-toggle ${justificationStatus}`} onClick={onToggleJustification} aria-expanded={justificationExpanded} title={justificationStatus === "pending" ? "Agregar sustento obligatorio" : "Editar sustento"} aria-label={`${justificationStatus === "pending" ? "Agregar" : "Editar"} sustento de ${task.taskCode}`}><MessageSquareText /></button>}</div></div><div className="task-metadata" aria-label={`Unidad ${task.unit}, versión vigente MOD ${String(task.baselineVersionNumber).padStart(2, "0")}`}><span className="task-metadata-tag unit-tag">{task.unit}</span><span className="task-metadata-tag version-tag">MOD {String(task.baselineVersionNumber).padStart(2, "0")}</span></div></td>
      {task.baseline.map((original, index) => {
        const changed = task.modified[index] !== null;
        const monthEnabled = Boolean(workspace.cycle.months[index]?.enabled) && (!workspace.allowedMonths || workspace.allowedMonths.includes(index + 1));
        const modified = task.modified[index];
        const direction = modified === null ? null : modified > original ? "up" : "down";
        return <td key={index} className={changed ? "changed-cell" : ""}><div className="target-input"><span>{original}</span><div className="modified-input-wrap"><input type="number" min="0" step="1" value={modified ?? original} disabled={locked || !monthEnabled} onChange={(e) => onChange(task.id, index, e.target.value)} onBlur={() => onCommit(task, index)} aria-label={`${task.taskCode} ${MONTHS[index]}`} />{direction && <span className={`change-direction ${direction}`} title={direction === "up" ? "La meta aumenta" : "La meta disminuye"} aria-label={direction === "up" ? "La meta aumenta" : "La meta disminuye"}>{direction === "up" ? "↑" : "↓"}</span>}</div></div></td>;
      })}
      <td className={total !== originalTotal ? "total-changed" : ""}><strong>{total}</strong><small>Antes {originalTotal}</small></td>
    </tr>
  );
}

function TaskJustificationRow({ task, value, locked, onChange, onCommit }: {
  task: TaskRow;
  value: string;
  locked: boolean;
  onChange: (taskId: string, value: string) => void;
  onCommit: (taskId: string) => void;
}) {
  const complete = value.trim().length >= 5;
  return <tr className="inline-justification-row"><td colSpan={MONTHS.length + 2}><label><div className="justification-link"><CornerDownRight aria-hidden="true" /><span>Sustento</span></div><textarea value={value} disabled={locked} onChange={(event) => onChange(task.id, event.target.value)} onBlur={() => void onCommit(task.id)} placeholder="Explique el motivo del cambio…" rows={2} aria-label={`Sustento de ${task.taskCode}`} /><small className={complete ? "complete" : "pending"}>{complete ? "Sustento completo" : "Obligatorio · mínimo 5 caracteres"}</small></label></td></tr>;
}

function TaskDefinitionDrawer({ task, onClose }: { task: TaskRow; onClose: () => void }) {
  const formattedDate = task.definitionUpdatedOn
    ? new Intl.DateTimeFormat("es-PE", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${task.definitionUpdatedOn}T00:00:00Z`))
    : null;
  return (
    <div className="definition-drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside className="definition-drawer" role="dialog" aria-modal="true" aria-labelledby="definition-drawer-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="definition-drawer-header">
          <div className="definition-drawer-icon"><BookOpenText /></div>
          <div><span>INFORMACIÓN OPERACIONAL</span><h2 id="definition-drawer-title">{task.taskCode}</h2><p>{task.taskName}</p></div>
          <button type="button" onClick={onClose} aria-label="Cerrar definición"><X /></button>
        </header>
        <div className="definition-drawer-content">
          <section className="definition-primary"><span>Definición operacional de la tarea</span><p>{task.operationalDefinition || "Esta tarea aún no tiene una definición operacional registrada."}</p></section>
          {task.criteria && <section className="definition-criteria"><span>Criterio</span><p>{task.criteria}</p></section>}
          {task.activityOperationalDefinition && <section className="definition-section"><span>Actividad operativa</span><strong>{task.activityCode} · {task.activityName}</strong><p>{task.activityOperationalDefinition}</p></section>}
          {task.normativeBasis && <details className="definition-normative"><summary>Base normativa</summary><p>{task.normativeBasis}</p></details>}
        </div>
        <footer className="definition-drawer-footer">
          <div><span>Unidad de medida</span><strong>{task.unit}</strong></div>
          {task.definitionResponsible && <div><span>Responsable de actualización</span><strong>{task.definitionResponsible}</strong></div>}
          {formattedDate && <div><span>Actualizada</span><strong>{formattedDate}</strong></div>}
        </footer>
      </aside>
    </div>
  );
}

function SummaryCard({ label, value, detail, icon, accent = false }: { label: string; value: string | number; detail: string; icon: React.ReactNode; accent?: boolean }) {

  return <div className={`summary-card ${accent ? "accent" : ""}`}><div className="summary-icon">{icon}</div><div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div></div>;
}

export function LegacyAdminPanel({ workspace, onWorkspaceChange }: { workspace: WorkspaceData; onWorkspaceChange: React.Dispatch<React.SetStateAction<WorkspaceData | null>> }) {
  async function toggleRegistration() {
    const enabled = !workspace.cycle.registrationEnabled;
    if (isSupabaseConfigured) await setRegistrationEnabled(workspace.cycle.id, enabled);
    onWorkspaceChange((current) => current ? { ...current, cycle: { ...current.cycle, registrationEnabled: enabled } } : current);
  }
  async function toggleMonth(monthIndex: number) {
    const enabled = !workspace.cycle.months[monthIndex].enabled;
    if (isSupabaseConfigured) await setMonthEnabled(workspace.cycle.id, monthIndex + 1, enabled);
    onWorkspaceChange((current) => current ? { ...current, cycle: { ...current.cycle, months: current.cycle.months.map((month, index) => index === monthIndex ? { ...month, enabled } : month) } } : current);
  }
  return (
    <div className="page admin-page">
      <header className="page-header"><div><span className="eyebrow">CONFIGURACIÓN DEL PROCESO</span><h1>Administración</h1><p>Controla la ventana de registro y los periodos que pueden modificarse.</p></div><span className="admin-badge"><ShieldCheck />Acceso administrador</span></header>
      <section className="admin-grid">
        <div className="admin-card wide">
          <div className="card-heading"><div className="heading-icon"><SlidersHorizontal /></div><div><h2>Ventana de registro</h2><p>El bloqueo general prevalece sobre la configuración mensual.</p></div></div>
          <div className="setting-row"><div><strong>Permitir reprogramaciones</strong><span>Los responsables podrán guardar y cerrar sus cambios.</span></div><button className={`switch ${workspace.cycle.registrationEnabled ? "on" : ""}`} onClick={() => void toggleRegistration()}><span /></button></div>
        </div>
        <div className="admin-card stat-card"><Users /><strong>{workspace.costCenters.length}</strong><span>Centros visibles</span></div>
        <div className="admin-card stat-card"><FileSpreadsheet /><strong>{workspace.tasks.length}</strong><span>Tareas en el centro actual</span></div>
      </section>
      <section className="admin-card months-card">
        <div className="card-heading"><div className="heading-icon"><Settings2 /></div><div><h2>Meses habilitados</h2><p>Deshabilitar un mes bloquea su edición en todos los centros de costo.</p></div></div>
        <div className="month-grid">{MONTHS.map((month, index) => <button key={month} className={workspace.cycle.months[index]?.enabled ? "enabled" : ""} onClick={() => void toggleMonth(index)}><span>{month}</span><small>{workspace.cycle.months[index]?.enabled ? "Habilitado" : "Bloqueado"}</small><div className="mini-switch"><i /></div></button>)}</div>
      </section>
      <section className="coming-soon"><Users /><div><strong>Gestión de usuarios y monitoreo por centro</strong><span>La estructura de seguridad ya está preparada; esta será la siguiente pantalla del módulo administrador.</span></div></section>
    </div>
  );
}

function Splash({ message }: { message: string }) {
  return <div className="splash"><div className="brand-mark"><FileSpreadsheet /></div><div className="spinner" /><span>{message}</span></div>;
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="splash"><div className="error-icon"><X /></div><h2>No pudimos abrir el aplicativo</h2><p>{message}</p><button className="primary-button" onClick={onRetry}>Intentar nuevamente</button></div>;
}

export default App;
